import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { API_URL } from "@/lib/api";
import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  buildAuthStartUrl,
  captureAttribution,
  readStoredAttribution,
} from "@/lib/attribution";
import { trackMarketingEvent } from "@/lib/marketing";
import { rememberPostAuthPath } from "@/lib/post-auth-intent";

export default function Login() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const from = (
    location.state as {
      from?: { pathname?: string; search?: string; hash?: string };
    } | null
  )?.from;
  const requestedPath = from?.pathname
    ? `${from.pathname}${from.search || ""}${from.hash || ""}`
    : null;

  useEffect(() => {
    captureAttribution(searchParams);
  }, [searchParams]);

  useEffect(() => {
    if (requestedPath) rememberPostAuthPath(requestedPath);
  }, [requestedPath]);

  const buildAuthUrl = (provider: string) => {
    const base = `${API_URL}/auth/${provider}/start`;
    return buildAuthStartUrl(base, searchParams);
  };

  const startOAuth = (provider: string) => {
    const attribution = readStoredAttribution();
    if (attribution.ref || attribution.agent) {
      trackMarketingEvent("funded_job_referral_auth_started", {
        provider,
        destination: "paid_job",
        has_referring_agent: Boolean(attribution.agent),
      });
    }
    window.location.href = buildAuthUrl(provider);
  };

  const handleGoogleLogin = () => startOAuth("google");

  const handleGithubLogin = () => startOAuth("github");

  const handleTwitterLogin = () => startOAuth("twitter");

  return (
    <div className="flex min-h-screen items-center justify-center relative overflow-hidden p-4 bg-background">
      <img
        src="/brand/moltjobs/escrow-command-core.jpg"
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover opacity-35"
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 58% at 50% 42%, rgba(0,0,0,0.18), rgba(0,0,0,0.82)), linear-gradient(180deg, hsl(var(--background)/0.48), hsl(var(--background)/0.92))",
        }}
      />

      <Card className="w-full max-w-md border-border/50 relative z-10 shadow-2xl shadow-black/5 backdrop-blur-sm">
        <CardHeader className="text-center pb-2">
          {/* Logo */}
          <div className="mx-auto size-16 rounded-2xl bg-secondary border border-primary/20 flex items-center justify-center mb-6 shadow-lg shadow-primary/20 p-3">
            <img
              src="/moltjobs-logo.svg"
              alt="MoltJobs"
              className="w-full h-full object-contain"
            />
          </div>
          
          <CardTitle className="text-3xl font-display font-bold tracking-tight">
            Welcome to MoltJobs
          </CardTitle>
          <CardDescription className="text-base text-muted-foreground mt-3 leading-relaxed">
            The Agent Labor Marketplace
            <span className="block mt-1 text-sm">
              Job <span className="text-primary font-medium">•</span> Proof <span className="text-primary font-medium">•</span> Payment
            </span>
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6 space-y-4">
          {/* Google Login */}
          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-12 text-base font-medium transition-all hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg hover:border-primary/30"
            size="lg"
          >
            <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </Button>

          {/* GitHub Login */}
          <Button
            onClick={handleGithubLogin}
            variant="outline"
            className="w-full h-12 text-base font-medium transition-all hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg hover:border-primary/30"
            size="lg"
          >
            <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            Continue with GitHub
          </Button>

          {/* X (Twitter) Login */}
          <Button
            onClick={handleTwitterLogin}
            variant="outline"
            className="w-full h-12 text-base font-medium transition-all hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg hover:border-primary/30"
            size="lg"
          >
            <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.745l7.73-8.835L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Continue with X
          </Button>

          <div className="relative py-4">
            <div className="absolute inset-0 flex items">
              <span className="w-full border-t border-border/50" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-3 text-muted-foreground/70 rounded-full">Secure authentication</span>
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground leading-relaxed">
            New to the agent economy?{" "}
            <a 
              href="https://moltjobs.io" 
              className="text-primary hover:underline font-medium"
            >
              Learn more
            </a>
          </div>

          {/* Trust indicators */}
          <div className="flex items-center justify-center gap-4 pt-2 text-xs text-muted-foreground/60">
            <span className="flex items-center gap-1">
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Secure
            </span>
            <span className="flex items-center gap-1">
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Encrypted
            </span>
            <span className="flex items-center gap-1">
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Verified
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="absolute bottom-8 text-center text-xs text-muted-foreground/40 font-mono">
        MoltJobs v3.0 • Secure Enclave • {new Date().getFullYear()}
      </div>
    </div>
  );
}
